import React from 'react';
import { Alert } from '@mui/material';
import type { UseFormRegister, FieldErrors } from 'react-hook-form';
import MaskedInput, { Mask } from 'react-text-mask';
import { Button, CARD_LOGO, Input } from '../../../ui';
import { ErrorIcon } from '../../../ui/icons';
import { assetUrl } from '../../../../utils/webConfig';
import { getCardsThumbFormat } from '../../../../utils/cardUtils';
import type { CardThumb } from '../../../Donee/components/common/constants';
import type { CardFormValues } from './useCardStep';

type AuthCardStepViewProps = {
    register: UseFormRegister<CardFormValues>;
    errors: FieldErrors<CardFormValues>;
    loading?: boolean;
    cardThumb?: CardThumb;
    showNameOnCard?: boolean;
    onSubmit?: React.FormEventHandler<HTMLFormElement>;
    error?: string | null;
    dismissError?: () => void;
};

const expiryMask = [/[01]/, /[0-9]/, '/', /[2-9]/, /[0-9]/];

const ExpiryMaskedInput = React.forwardRef<
    HTMLInputElement,
    React.InputHTMLAttributes<HTMLInputElement>
>(function ExpiryMaskedInput(props, forwardedRef) {
    return (
        <MaskedInput
            {...props}
            mask={expiryMask}
            guide={false}
            placeholderChar=" "
            render={(maskRef, maskProps) => (
                <input
                    {...props}
                    onChange={
                        maskProps.onChange as React.ChangeEventHandler<HTMLInputElement>
                    }
                    onBlur={
                        maskProps.onBlur as React.FocusEventHandler<HTMLInputElement>
                    }
                    defaultValue={maskProps.defaultValue}
                    ref={(el) => {
                        maskRef(el as HTMLElement);
                        if (typeof forwardedRef === 'function')
                            forwardedRef(el);
                        else if (forwardedRef) forwardedRef.current = el;
                    }}
                />
            )}
        />
    );
});

type DigitsMaskedInputProps =
    React.InputHTMLAttributes<HTMLInputElement> & {
        mask?: Mask | ((value: string) => Mask);
    };

const DigitsMaskedInput = React.forwardRef<
    HTMLInputElement,
    DigitsMaskedInputProps
>(function DigitsMaskedInput({ mask, ...props }, forwardedRef) {
    return (
        <MaskedInput
            {...props}
            mask={mask ?? getCardsThumbFormat()}
            guide={false}
            render={(maskRef, maskProps) => (
                <input
                    {...props}
                    onChange={
                        maskProps.onChange as React.ChangeEventHandler<HTMLInputElement>
                    }
                    onBlur={
                        maskProps.onBlur as React.FocusEventHandler<HTMLInputElement>
                    }
                    defaultValue={maskProps.defaultValue}
                    ref={(el) => {
                        maskRef(el as HTMLElement);
                        if (typeof forwardedRef === 'function')
                            forwardedRef(el);
                        else if (forwardedRef) forwardedRef.current = el;
                    }}
                />
            )}
        />
    );
});

const getCvcLength = (cardThumb?: CardThumb) => cardThumb?.cvcLength[0] ?? 3;

export const AuthCardStepView: React.FC<AuthCardStepViewProps> = ({
    register,
    errors,
    loading,
    cardThumb,
    showNameOnCard,
    onSubmit,
    error,
    dismissError,
}) => (
    <form id="auth-card-form" onSubmit={onSubmit}>
        <div
            id="auth-card-step"
            className="flex flex-col mt-8 w-full min-h-[412px]"
        >
            <div
                id="auth-card-heading"
                className="text-body-large text-primary mb-1"
            >
                Card Details
            </div>
            <div
                id="auth-card-security-message"
                className="flex items-center gap-2 mb-6 text-body-xsmall text-secondary cursor-pointer"
            >
                <img
                    id="auth-card-security-lock-icon"
                    src={assetUrl('/images/svg/lock-icon.svg')}
                    alt=""
                    width={12}
                    height={14}
                />
                Your information is secure
                <img
                    id="auth-card-security-details-icon"
                    src={assetUrl('/images/svg/history-list-arrow.svg')}
                    alt=""
                    width={9}
                    height={9}
                />
            </div>
            {showNameOnCard && (
                <Input
                    id="auth-card-name"
                    {...register('nameOnCard')}
                    label="Name on Card"
                    className="mb-3"
                    error={!!errors.nameOnCard}
                    helperText={errors.nameOnCard?.message}
                />
            )}
            <div id="auth-card-number-container" className="relative mb-3">
                <Input
                    id="auth-card-number"
                    {...register('cardNumber')}
                    label="Card Number"
                    inputMode="numeric"
                    error={!!errors.cardNumber}
                    helperText={errors.cardNumber?.message}
                    slotProps={{
                        input: {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            inputComponent: DigitsMaskedInput as any,
                        },
                        htmlInput: {
                            mask: getCardsThumbFormat(cardThumb?.type),
                        },
                    }}
                />
                {cardThumb && CARD_LOGO[cardThumb.type] && (
                    <img
                        id="auth-card-brand-logo"
                        className="absolute right-3 top-4 pointer-events-none"
                        src={assetUrl(CARD_LOGO[cardThumb.type])}
                        alt={cardThumb.type}
                        height={28}
                    />
                )}
            </div>
            <div id="auth-card-security-fields" className="flex gap-2">
                <Input
                    id="auth-card-expiry"
                    {...register('expiry')}
                    label="Exp Date (mm/yy)"
                    inputMode="numeric"
                    error={!!errors.expiry}
                    helperText={errors.expiry?.message}
                    slotProps={{
                        input: {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            inputComponent: ExpiryMaskedInput as any,
                        },
                    }}
                />
                <Input
                    id="auth-card-cvv"
                    {...register('cvv')}
                    label="CVV"
                    inputMode="numeric"
                    error={!!errors.cvv}
                    helperText={errors.cvv?.message}
                    maxLength={getCvcLength(cardThumb)}
                    slotProps={{
                        input: {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            inputComponent: DigitsMaskedInput as any,
                        },
                        htmlInput: {
                            mask: Array(getCvcLength(cardThumb)).fill(/\d/),
                        },
                    }}
                />
            </div>
            {error ? (
                <Alert
                    id="auth-card-error-alert"
                    className="my-6"
                    severity="error"
                    onClose={dismissError}
                    iconMapping={{ error: <ErrorIcon /> }}
                >
                    {error}
                </Alert>
            ) : null}
            <Button
                id="auth-card-next-button"
                type="submit"
                className="w-full mt-auto"
                loading={loading}
            >
                Next
            </Button>
        </div>
    </form>
);
